import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, Star, Truck, Users, X } from "lucide-react";
import { nomChauffeur } from "./common";

function norm(s) {
  return (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Menu déroulant avec barre de recherche : les chauffeurs liés à la tournée
 * choisie apparaissent en premier (principaux puis secondaires).
 */
export function ChauffeurPicker({ chauffeurs, tournee, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  const selected = chauffeurs.find((c) => c.id === value);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const { lies, autres } = useMemo(() => {
    const s = norm(q.trim());
    const match = (c) => !s || norm(`${c.prenom} ${c.nom} ${c.nom} ${c.prenom} ${c.telephone} ${c.sst}`).includes(s);
    const sorter = (a, b) => (a.role !== b.role ? (a.role === "principal" ? -1 : 1)
      : (a.tournee_defaut === tournee ? -1 : 0) - (b.tournee_defaut === tournee ? -1 : 0) || a.nom.localeCompare(b.nom));
    const l = chauffeurs.filter((c) => tournee && (c.tournees || []).includes(tournee) && match(c)).sort(sorter);
    const ids = new Set(l.map((c) => c.id));
    const a = chauffeurs.filter((c) => !ids.has(c.id) && match(c)).sort((x, y) => x.nom.localeCompare(y.nom));
    return { lies: l, autres: a };
  }, [chauffeurs, tournee, q]);

  const Row = ({ c }) => (
    <button
      type="button"
      onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent ${c.id === value ? "bg-accent" : ""}`}
      data-testid={`chauffeur-opt-${c.id}`}
    >
      {c.role === "principal" ? <Truck size={13} className="text-primary shrink-0" /> : <Users size={13} className="text-muted-foreground shrink-0" />}
      <span className="font-medium truncate">{nomChauffeur(c)}</span>
      {tournee && c.tournee_defaut === tournee && <Star size={11} className="text-primary shrink-0" fill="currentColor" />}
      <span className="ml-auto text-[11px] text-muted-foreground font-mono shrink-0">{(c.tournees || []).join(", ")}</span>
      {c.id === value && <Check size={14} className="text-primary shrink-0" />}
    </button>
  );

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-10 px-3 rounded-md border border-input bg-transparent text-sm flex items-center gap-2 text-left"
        data-testid="chauffeur-picker"
      >
        {selected ? (
          <>
            {selected.role === "principal" ? <Truck size={14} className="text-primary" /> : <Users size={14} className="text-muted-foreground" />}
            <span className="truncate">{nomChauffeur(selected)}</span>
          </>
        ) : <span className="text-muted-foreground">Choisir le chauffeur…</span>}
        <span className="ml-auto flex items-center gap-1">
          {selected && (
            <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange(null); }} className="p-0.5 rounded hover:bg-accent text-muted-foreground"><X size={14} /></span>
          )}
          <ChevronDown size={15} className="text-muted-foreground" />
        </span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-border bg-popover shadow-xl overflow-hidden" data-testid="chauffeur-list">
          <div className="flex items-center gap-2 px-3 border-b border-border">
            <Search size={14} className="text-muted-foreground" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un chauffeur…" className="h-9 flex-1 bg-transparent outline-none text-sm" data-testid="chauffeur-search" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {lies.length > 0 && (
              <>
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-primary font-semibold">Chauffeurs de la tournée {tournee}</p>
                {lies.map((c) => <Row key={c.id} c={c} />)}
              </>
            )}
            {autres.length > 0 && (
              <>
                <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{lies.length ? "Autres chauffeurs" : "Chauffeurs"}</p>
                {autres.map((c) => <Row key={c.id} c={c} />)}
              </>
            )}
            {!lies.length && !autres.length && <p className="px-3 py-3 text-sm text-muted-foreground">Aucun chauffeur trouvé.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
